import { createFileRoute } from '@tanstack/react-router'
import { observer } from 'mobx-react-lite'
import useView from '@/hooks/useView.js'
import { useEffect } from 'react'
import { LoadingIndicator, LoadingIndicatorTitle } from '@/components/ui/loadingIndicator.jsx'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.jsx'
import ReportsTable from '@/components/reportsTable/reportsTable.jsx'
import PurchaseTableHeader from '@/components/reportsTable/purchaseTableHeader.jsx'
import PurchaseRow from '@/components/reportsTable/purchaseRow.jsx'
import RevenueTableHeader from '@/components/reportsTable/revenueTableHeader.jsx'
import RevenueRow from '@/components/reportsTable/revenueRow.jsx'
import TransactionTableHeader from '@/components/reportsTable/transactionTableHeader.jsx'
import TransactionRow from '@/components/reportsTable/transactionRow.jsx'
import { cn } from '@/utils/ui.js'
import { Currency } from '@/components/ui/currency.jsx'
import Icon from '@mdi/react'
import { mdiTransferDown, mdiTransferUp } from '@mdi/js'

const Stat = ({ amount, amountCompact, children, className, iconPath, ...props }) => (
  <div className={cn('flex flex-1 items-center justify-center gap-2', className)} {...props}>
    {iconPath && <Icon className="shrink-0" path={iconPath} size={2} />}

    <div className="flex flex-col">
      <Currency amount={amount} amountCompact={amountCompact} className="text-2xl" />
      <span className="text-muted-foreground text-sm">{children}</span>
    </div>
  </div>
)

const ReportsPage = () => {
  const { reportsView } = useView()

  useEffect(() => {
    reportsView.init().then()

    return () => {
      reportsView.reset()
    }
  }, [reportsView])

  if (!reportsView.isInitialized)
    return (
      <LoadingIndicator>
        <LoadingIndicatorTitle>Loading...</LoadingIndicatorTitle>
      </LoadingIndicator>
    )

  return (
    <div className="animate-in fade-in mx-auto w-full max-w-[1200px] px-3 duration-1000">
      <div className="flex justify-evenly gap-4">
        <Stat
          amount={reportsView.totalRevenueFormatted}
          amountCompact={reportsView.totalRevenueFormattedCompact}
          className="text-success"
          iconPath={mdiTransferDown}>
          earned
        </Stat>

        <Stat
          amount={reportsView.totalPurchasedFormatted}
          amountCompact={reportsView.totalPurchasedFormattedCompact}
          className="text-destructive"
          iconPath={mdiTransferUp}>
          paid
        </Stat>
      </div>

      <Tabs className="mt-6" defaultValue="purchases">
        <TabsList>
          <TabsTrigger value="purchases">Purchases</TabsTrigger>
          <TabsTrigger value="revenue">Store Revenue</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
        </TabsList>

        <TabsContent value="purchases">
          <ReportsTable
            csv={reportsView.getPurchasesCsv}
            csvName="purchases.csv"
            data={reportsView.purchases}
            header={PurchaseTableHeader}
            row={PurchaseRow}
          />
        </TabsContent>

        <TabsContent value="revenue">
          <ReportsTable
            csv={reportsView.getRevenueCsv}
            csvName="revenue.csv"
            data={reportsView.revenue}
            header={RevenueTableHeader}
            row={RevenueRow}
          />
        </TabsContent>

        <TabsContent value="transactions">
          <ReportsTable
            csv={reportsView.getTransactionsCsv}
            csvName="transactions.csv"
            data={reportsView.transactions}
            header={TransactionTableHeader}
            row={TransactionRow}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/reports')({
  component: observer(ReportsPage),
  staticData: {
    seo: {
      title: 'Reports',
    },
  },
})
